// Hàm thuần cho tab "Đề cử & giải": nhãn trạng thái công bố, đếm người sẽ nhận push, xem trước dán danh sách.
// Luật bóc UID dùng lại `normalizeUid` của đợt 1 (lib/paste-preview.ts).

import type { AnnounceStage, GalaTicketStatus, NomineeResult } from '../contract';
import { normalizeUid } from './paste-preview';

const NOT_ANNOUNCED = 'Chưa công bố';
const VN_OFFSET_MS = 7 * 3600e3;
const pad = (n: number) => String(n).padStart(2, '0');

/** ISO UTC -> 'HH:mm DD/MM' giờ Việt Nam (+07:00); null khi rỗng hoặc sai. */
function stamp(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const ms = new Date(iso).getTime();
  if (Number.isNaN(ms)) return null;
  const d = new Date(ms + VN_OFFSET_MS);
  return `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())} ${pad(d.getUTCDate())}/${pad(
    d.getUTCMonth() + 1
  )}`;
}

const label = (iso: string | null | undefined) => {
  const s = stamp(iso);
  return s ? `Đã công bố ${s}` : NOT_ANNOUNCED;
};

/** Hai nhãn trạng thái công bố của một hạng mục: đề cử và người thắng. */
export function nominationStatusLabel(cat: {
  nomineesAnnouncedAt: string | null;
  winnersAnnouncedAt: string | null;
}): { nominees: string; winners: string } {
  return { nominees: label(cat.nomineesAnnouncedAt), winners: label(cat.winnersAnnouncedAt) };
}

const RESULT_TEXT: Record<NomineeResult, string> = {
  PENDING: 'Chưa có kết quả',
  WINNER: 'Người thắng',
  NOT_WINNER: 'Không thắng',
};

export const resultLabel = (r: NomineeResult): string => RESULT_TEXT[r] ?? r;

interface MemberLike {
  ticketId: number;
  status: GalaTicketStatus;
  hasAccount: boolean;
}
interface NomineeLike {
  result: NomineeResult;
  members: MemberLike[];
}

/**
 * Số người TỐI ĐA sẽ nhận push khi công bố: vé ISSUED đã gắn tài khoản, mỗi vé một lần.
 * Công bố người thắng chỉ tính đơn vị WINNER. (Người đã được push trước đó hoặc tắt nhắc lịch sẽ không nhận lại.)
 */
export function announceRecipients(nominees: NomineeLike[], stage: AnnounceStage): number {
  const tickets = new Set<number>();
  nominees.forEach((n) => {
    if (stage === 'WINNERS' && n.result !== 'WINNER') return;
    n.members.forEach((m) => {
      if (m.status === 'ISSUED' && m.hasAccount) tickets.add(m.ticketId);
    });
  });
  return tickets.size;
}

/**
 * Công bố người thắng được khi sự kiện PUBLISHED hoặc ENDED (cron tự kết thúc 24 giờ
 * sau đêm Gala, người thắng vẫn công bố sau đó được). Công bố đề cử chỉ khi PUBLISHED.
 */
export function canAnnounce(stage: AnnounceStage, eventStatus: string): boolean {
  if (eventStatus === 'PUBLISHED') return true;
  return stage === 'WINNERS' && eventStatus === 'ENDED';
}

/** Tiêu đề push "Chúc mừng" như server (buildWinnerPush): tên giải, trống thì tên hạng mục. */
export function winnerPushTitle(
  prizeLabel: string | null | undefined,
  categoryName: string
): string {
  return `🏆 Chúc mừng! Bạn nhận ${(prizeLabel ?? '').trim() || categoryName}`;
}

/**
 * Tách cảnh báo khi dán: dòng BỊ BỎ QUA (trùng UID trong lần dán, đã ở đơn vị khác)
 * và dòng VẪN ĐƯỢC NHẬN (vd. vé chưa gắn tài khoản). Khớp chữ cảnh báo của server.
 */
export function splitPasteWarnings<T extends { reason: string }>(
  warnings: T[]
): { skipped: T[]; accepted: T[] } {
  const isSkip = (w: T) => /^Trùng UID|bỏ qua/i.test(w.reason);
  return { skipped: warnings.filter(isSkip), accepted: warnings.filter((w) => !isSkip(w)) };
}

export interface NomineePastePreview {
  /** Số dòng có chữ (dòng trống không tính). */
  total: number;
  /** Số dòng bóc được UID. */
  valid: number;
  /** Số dòng GỐC (từ 1, tính cả dòng trống) không bóc được UID. */
  invalid: number[];
  /** Số đơn vị sẽ tạo / cập nhật: mỗi tên đơn vị một đơn vị + mỗi dòng không tên một đơn vị riêng. */
  unitCount: number;
  /** Các nhóm có tên đơn vị (theo thứ tự xuất hiện) và số dòng hợp lệ trong nhóm. */
  groups: { name: string; count: number }[];
}

/** Ô đầu / phần còn lại của dòng: tách theo dấu phân cách đầu tiên có mặt (tab, rồi phẩy, rồi `|`). */
function splitFirst(line: string): [string, string] {
  const sep = ['\t', ',', '|'].find((s) => line.includes(s));
  if (!sep) return [line.trim(), ''];
  const at = line.indexOf(sep);
  return [line.slice(0, at).trim(), line.slice(at + 1).trim()];
}

/** Xem trước ô "Dán danh sách đề cử" (mỗi dòng `UID[, tên đơn vị]`): cùng luật bóc UID với server, gộp theo tên. */
export function pastePreview(text: string): NomineePastePreview {
  let total = 0;
  let valid = 0;
  let singles = 0;
  const invalid: number[] = [];
  const groups = new Map<string, { name: string; count: number }>();
  String(text ?? '')
    .split('\n')
    .forEach((raw, i) => {
      const line = raw.replace(/\r$/, '');
      if (line.trim() === '') return;
      total += 1;
      const [first, rest] = splitFirst(line);
      if (normalizeUid(first) === null) {
        invalid.push(i + 1);
        return;
      }
      valid += 1;
      const name = rest.split(/[\t,|]/)[0].trim().replace(/\s+/g, ' ');
      if (!name) {
        singles += 1;
        return;
      }
      const key = name.toLowerCase();
      const g = groups.get(key);
      if (g) g.count += 1;
      else groups.set(key, { name, count: 1 });
    });
  return {
    total,
    valid,
    invalid,
    unitCount: singles + groups.size,
    groups: Array.from(groups.values()),
  };
}
